import { Alert, Grid } from '@mui/material';
import { useState } from 'react';

import { PlanningWarning, PlanningWarningDialog } from '../../components/general/PlanningWarningDialog.tsx';
import {
  generateCreateDurchgangErrorSnackbar,
  generateCreateDurchgangErrorSnackbarWithFehlerId,
  generateCreateDurchgangSuccessSnackbar,
} from '../../components/snackbars/createDurchgangSnackbars.tsx';
import { useSnackbarFromGenerated } from '../../components/snackbars/useSnackbarFromGenerated.ts';
import { ThemaFromThemaRouter } from '../../dtos.ts';
import { trpc } from '../../trpc.ts';
import { sessionStorageApi, sessionStorageKeys } from '../../utils/index.ts';
import { VeranstaltungPlanungSidebar } from '../ThemaVeranstaltungPlanungPage/VeranstaltungPlanungSidebar';
import { content } from './CreateDurchgang.content.ts';
import { CreateDurchgang } from './CreateDurchgang.tsx';
import { initializePlanningData } from './CreateDurchgangInput/CreateDurchgangInput.utils.ts';
import { SaveButtonState } from './DurchgangPlanung.types.ts';
import { DurchgangPlanungHeader } from './DurchgangPlanungHeader.tsx';

type DurchgangPlanungProps = {
  readonly thema: ThemaFromThemaRouter;
};

const initialSaveButtonState = {
  tooltip: '',
  saveInputData: undefined,
  disabled: true,
};

export const DurchgangPlanung = ({ thema }: DurchgangPlanungProps) => {
  const { enqueueSnackbarFromGenerated } = useSnackbarFromGenerated();
  const [saveButtonState, setSaveButtonState] = useState<SaveButtonState>(initialSaveButtonState);
  const [planningWarnings, setPlanningWarnings] = useState<PlanningWarning[]>([]);
  const [isWarningDialogOpen, setIsWarningDialogOpen] = useState(false);

  const createDurchgangMutation = trpc.durchgang.createDurchgang.useMutation({
    onSuccess: (createDurchgangOutput) => {
      enqueueSnackbarFromGenerated(generateCreateDurchgangSuccessSnackbar(createDurchgangOutput.akaDurchgangId));
    },
    onError: (error) => {
      const fehlerId = sessionStorageApi.get(sessionStorageKeys.LATEST_X_VIVA_REQUEST_ID);

      enqueueSnackbarFromGenerated(
        fehlerId === null
          ? generateCreateDurchgangErrorSnackbar(error)
          : generateCreateDurchgangErrorSnackbarWithFehlerId({ error, fehlerId }),
      );
    },
  });

  const [planningData, setPlanningData] = useState(initializePlanningData(thema));

  const checkDurchgangAvailabilityQuery = trpc.durchgang.checkDurchgangAvailability.useMutation();

  const handleSave = async (): Promise<void> => {
    if (!saveButtonState.disabled && saveButtonState.saveInputData) {
      if (planningWarnings.length > 0) {
        setIsWarningDialogOpen(true);
        return;
      }

      await createDurchgangMutation.mutateAsync(saveButtonState.saveInputData);
    }
    resetStates();
  };

  const handleWarningDialogCancel = (): void => {
    setIsWarningDialogOpen(false);
  };

  const handleWarningDialogApprove = async (): Promise<void> => {
    setIsWarningDialogOpen(false);
    if (saveButtonState.saveInputData) {
      await createDurchgangMutation.mutateAsync(saveButtonState.saveInputData);
    }
    resetStates();
  };

  function resetStates() {
    setSaveButtonState(initialSaveButtonState);
    setPlanningData((prevData) => ({
      ...prevData,
      module: prevData.module.map((modul) => ({
        ...modul,
        datum: null,
      })),
    }));
    setPlanningWarnings([]);
    checkDurchgangAvailabilityQuery.reset();
  }

  if (!thema.planungsinformation) {
    return <Alert severity="warning">{content.missingPlanningInfo}</Alert>;
  }

  return (
    <Grid container spacing={2}>
      <DurchgangPlanungHeader
        akaThemaId={thema.akaThemaId}
        disableSaveButton={saveButtonState.disabled || createDurchgangMutation.isPending}
        saveButtonTooltip={saveButtonState.tooltip}
        handleSave={handleSave}
      />
      <Grid size={{ xs: 12 }} container spacing={2}>
        <Grid sx={{ width: 320, flexShrink: 0 }}>
          <VeranstaltungPlanungSidebar thema={thema} />
        </Grid>
        <Grid sx={{ flex: 1, minWidth: 0 }}>
          <CreateDurchgang
            thema={thema}
            setSaveButtonState={setSaveButtonState}
            planningData={planningData}
            setPlanningData={setPlanningData}
            onPlanningWarningsChange={setPlanningWarnings}
            checkDurchgangAvailabilityQuery={checkDurchgangAvailabilityQuery}
          />
        </Grid>
      </Grid>
      <PlanningWarningDialog
        open={isWarningDialogOpen}
        warnings={planningWarnings}
        onApprove={handleWarningDialogApprove}
        onCancel={handleWarningDialogCancel}
        loading={createDurchgangMutation.isPending}
      />
    </Grid>
  );
};
